/**
 * Onboarding Screen 2: Home Preview
 * Show sample Home feed with animated cards
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Sun, Link2, Flame, User, TrendingUp } from 'lucide-react';
import { sampleHomeData } from '../data/sampleData';

interface HomePreviewProps {
  onNext: () => void;
}

const iconMap: Record<string, React.ElementType> = {
  sparkles: Sparkles,
  sun: Sun,
  link: Link2,
  flame: Flame
};

export const HomePreview: React.FC<HomePreviewProps> = ({ onNext }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-gray-50 to-white p-8 overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          This is your Home
        </h2>
        <p className="text-gray-600">
          Your personalized feed of insights, connections, and momentum
        </p>
      </motion.div>

      {/* Content Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex-1 space-y-6 max-w-4xl mx-auto w-full"
      >
        {/* Weekly Summary Card */}
        <motion.div variants={itemVariants} className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5" />
                <span className="text-sm font-medium opacity-90">Weekly Summary</span>
              </div>
              <h3 className="text-2xl font-bold mb-1">
                {sampleHomeData.weeklySummary.title}
              </h3>
              <p className="text-white/80">
                {sampleHomeData.weeklySummary.items} items explored across {sampleHomeData.weeklySummary.topTopics.join(', ')}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Insight Cards */}
        <div className="grid grid-cols-2 gap-4">
          {sampleHomeData.insightCards.slice(0, 4).map((card, index) => {
            const Icon = iconMap[card.icon] || Sparkles;
            return (
              <motion.div
                key={card.id}
                variants={itemVariants}
                className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${card.gradient} flex items-center justify-center mb-3`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  {card.title}
                </h4>
                <p className="text-sm text-gray-600">
                  {card.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Recent Conversation */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900">Recent Conversation</span>
                <span className="text-xs text-gray-500">{sampleHomeData.recentConversation.time}</span>
              </div>
              <p className="text-sm text-gray-700 font-medium mb-1">
                "{sampleHomeData.recentConversation.message}"
              </p>
              <p className="text-xs text-gray-600">
                {sampleHomeData.recentConversation.preview}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Suggested Connection */}
        <motion.div variants={itemVariants} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                {sampleHomeData.suggestedConnection.name.charAt(0)}
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Suggested connection</p>
                <p className="font-semibold text-gray-900">{sampleHomeData.suggestedConnection.name}</p>
                <p className="text-xs text-gray-600">
                  {sampleHomeData.suggestedConnection.commonTopics.slice(0, 2).join(', ')} · {sampleHomeData.suggestedConnection.mutualConnections} mutual
                </p>
              </div>
            </div>
            <button className="px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors">
              Connect
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Next Button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-8 flex justify-center"
      >
        <button
          onClick={onNext}
          className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-105"
        >
          Next
        </button>
      </motion.div>
    </div>
  );
};





